/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { PagingOptions } from '../../../types/PagingOptions';

export class SearchStateModel {
    searches: SearchListEntry[];
    definitions: SearchDefinition[];
    instances: {
        [id: string]: {
            search: SearchListEntry;
            criteria: SearchFormModel;
            params: SearchParamModel;
            viewState: SearchViewState;
            pagingOptions: PagingOptions;
        };
    };
}

export interface SearchDefinition {
    name: string;
    layer: string;
    layerField?: string;
    displayname: string;
    fields: SearchField[];
    resultFields: SearchResultField[];
    objectkey: string;
    dbKey: string;
    ispostgres?: boolean;
    isexcelenabled?: boolean;
    totalFields?: string[];
    averageFields?: string[];
    disablePrint?: boolean;
}

export interface SearchListEntry {
    name: string;
    displayName: string;
    sortOrder: number;
}

export type SearchFieldValue = SearchFieldValueText | SearchFieldValueNumeric | SearchFieldValueDate;

export interface SearchField {
    fieldValue: SearchFieldValue;
    operator: SearchOperator;
}

export interface SearchFieldValueBase {
    name: string;
    display: string;
    type: 'string' | 'numeric' | 'date';
    example: string | number;
    uitype: 'input' | 'dropdown' | 'date';
    listitems?: SearchFieldValueOption[];
    value?: any;
}

export interface SearchFieldValueText extends SearchFieldValueBase {
    type: 'string';
    operators: SearchOperatorText[];
}

export interface SearchFieldValueNumeric extends SearchFieldValueBase {
    type: 'numeric';
    operators: SearchOperatorNumeric[];
}

export interface SearchFieldValueDate extends SearchFieldValueBase {
    type: 'date';
    operators: SearchOperatorDate[];
}

export interface SearchFieldValueOption {
    value: any;
    label: string;
}

export interface SearchOperator {
    name: string;
    value?: any;
    operators: SearchOperatorOption[];
}

export interface SearchOperatorOptionBase {
    display: string;
}

export type SearchOperatorOption = SearchOperatorDate | SearchOperatorText | SearchOperatorNumeric;

export interface SearchOperatorText extends SearchOperatorOptionBase {
    type: 'equals' | 'contains' | 'begins' | 'hasvalue' | 'hasnovalue';
}

export interface SearchOperatorNumeric extends SearchOperatorOptionBase {
    type: 'equals' | 'gt' | 'gte' | 'lt' | 'lte' | 'hasvalue' | 'hasnovalue';
}

export interface SearchOperatorDate extends SearchOperatorOptionBase {
    type: 'hasvalue' | 'hasnovalue';
}

export interface SearchFormModel {
    fields: SearchField[];
}

export interface SearchParamModel {
    fields: SearchParam[];
}

export interface SearchParam {
    [name: string]: any;
}

export interface SearchResultField {
    name: string;
    type: string;
    sql: string;
    displayName: string;
}
export interface SearchResults {
    displayName: string;
    resultFields: SearchResultField[];
    objectkey: string;
    keytype: string;
    dbkey: string;
    layerfield: string;
    count: number;
    wantsfeatures: boolean;
    data: object[];
    totals?: any[];
    averages?: any[];
}

export interface ChooseSearchPayload {
    id: string;
    search: SearchListEntry;
    reset: boolean;
}

export interface CriteriaPayload {
    instanceId: string;
    criteria: SearchFormModel;
}

export interface SearchParamPayload {
    instanceId: string;
    params: SearchParamModel;
}

export type SearchViewState = 'criteria' | 'results';

export interface SearchViewPayload {
    instanceId: string;
    view: SearchViewState;
}

export interface ClearSearchResultFeaturesPayload {
    displayLayer: string;
}
